import { useEffect, useState } from 'react';
import { Animated } from 'react-native';
import { Banner } from '@/components/Banner';
import { Text } from '@/components/Text';
import theme from '@/config/theme';
import { useFadeOut } from '@/hooks/useFadeOut';
import { useDashboardContext } from '@/contexts/DashboardContext/useDashboardContext';
import { useStoreState } from '@/stores/store';
import encryptedStorage from '@/stores/encryptedStorage';
import { isExerciseSet } from '@/utils/isExerciseSet';
import { setStorageKey } from '@/utils/setStorageKey';
import {
  SentenceSetFromApi,
  TextSetFromApi,
  WordSetFromApi,
} from '@/stores/store.types';

const storage = encryptedStorage();

type DownloadBannerProps = {
  wordsets: WordSetFromApi[];
  sentencesets: SentenceSetFromApi[];
  textsets: TextSetFromApi[];
};

export const DownloadBanner = ({
  wordsets,
  sentencesets,
  textsets,
}: DownloadBannerProps) => {
  const { downloadedExercises } = useDashboardContext();
  const refresh = useStoreState((state) => state.refresh);

  const [hasSyncedSet, setHasSyncedSet] = useState<boolean | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadSyncStatus = async () => {
      const setKeys = [
        ...wordsets.map(({ _id }) => setStorageKey(_id, 'word')),
        ...sentencesets.map(({ _id }) => setStorageKey(_id, 'sentence')),
        ...textsets.map(({ _id }) => setStorageKey(_id, 'text')),
      ];
      const storedSets = await Promise.all(
        setKeys.map((setKey) => storage.getItem(setKey)),
      );
      if (!isMounted) {
        return;
      }
      setHasSyncedSet(
        storedSets.some(
          (storedSet) => isExerciseSet(storedSet) && storedSet.sync,
        ),
      );
    };

    loadSyncStatus();

    return () => {
      isMounted = false;
    };
  }, [wordsets, sentencesets, textsets, refresh, downloadedExercises]);

  const { opacity, isVisible } = useFadeOut(hasSyncedSet === false);

  if (!isVisible) {
    return null;
  }

  return (
    <Animated.View style={{ opacity }}>
      <Banner
        title="Laden Sie Ihre Sets herunter"
        icon="cloudDownload"
        backgroundColor={theme.colors.white}
        titleTextColor={theme.colors.newUI.therapist.textBlue100}
        borderColor={theme.colors.newUI.therapist.blue60}
      >
        <Text>
          Um die Übungen zu nutzen, laden Sie diese bitte zuerst herunter.
          Melden Sie sich ab, müssen die Übungen bei der nächsten Anmeldung
          erneut heruntergeladen werden.
        </Text>
      </Banner>
    </Animated.View>
  );
};
